<template>
  <div>
    <!-- Password generator -->
    <div>
      <h4>1. Select at least 3 words to use in your password</h4>
      <div>
        <button @click.prevent="refreshWordlist()" class="btn btn-light border-dark">Refresh Wordlist</button>
      </div>
      <br/>
      <table v-if="wordListFilled" class="table table-bordered table-striped hoverPointerTDs">
        <tbody>
        <tr v-for="row in 5">
          <td v-for="col in 3" :key="col" @click="selectWord(row, col)" :class="{ 'selected-word': wordsSelected[row - 1][col - 1] }">{{ wordList2D[row - 1][col - 1] }}</td>
        </tr>
        </tbody>
      </table>
    </div>
    <br/>
    <div>
      <h4>2. Generate your password</h4>
      <div>
        <button :disabled="generateButtonDisabled" @click.prevent="generatePassword()" class="btn btn-light border-dark">Generate</button>
      </div>
        <br>
        After you've finished selecting your words, click generate until you find a password you like.
      <copy-paste-input :text="password" :value="password" :show-check="clickedCopyInput === index" @click="passwordSelected(password, index)"  v-for="[index, password] in generatedPasswords.entries()" :key="index"></copy-paste-input>
    </div>
    <br/>
    <div>
      <h4>3. Choose a password</h4>
      <span v-if="genericMessage">Pick a password from the list above and then either copy or type it in as your new password wherever you would like.</span>
      <span v-if="!genericMessage">Pick a password from the list above and copy or type it in the "{{ passwordLabel }}" field below.</span>
    </div>
  </div>
</template>

<style scoped>
  .selected-word {
    background-color: #027891;
    color: white;
  }
  .hoverPointerTDs td:hover { cursor:pointer }
</style>

<script>
  import { mapState } from 'vuex'
  import CopyPasteInput from "./CopyPasteInput.vue";

  export default {
    name: 'PasswordGenerator',
    components: {CopyPasteInput},

    props: {
      genericMessage: {
        default: false,
        type: Boolean
      },
      passwordLabel: {
        default: 'New password',
        type: String
      }
    },

    data () {
      return {
        minNumbers: 2,
        symbolList: ['!', '@', '#', '$', '%', '^', '&', '*', '(', ')', '-', '_', '+', '=', '/', '[', ']', '{', '}', '<', '>', ',', '.', '?'],
        generatedPasswords: [],
        clickedCopyInput: null,
        wordsSelected: [[false, false, false],
          [false, false, false],
          [false, false, false],
          [false, false, false],
          [false, false, false]]

      }
    },

    computed: {
      wordListFilled () {
        return this.passwordWordList.length === 15
      },
      generateButtonDisabled () {
        return this.wordsSelectedList.length < 3
      },
      wordList2D () {
        if (!this.wordListFilled) {
          return []
        }
        let wordList = []
        let passwordWordListCopy = this.passwordWordList.slice()
        // Convert 1D wordlist to 2D wordlist to simplify display
        for (var row = 0; row < 5; row++) {
          let rowList = []
          for (var column = 0; column < 3; column++) {
            rowList.push(passwordWordListCopy.pop())
          }
          wordList.push(rowList)
        }
        return wordList
      },
      wordsSelectedList () {
        let selectedList = []
        for (var row = 0; row < 5; row++) {
          for (var col = 0; col < 3; col++) {
            if (this.wordsSelected[row][col]) {
              selectedList.push(this.wordList2D[row][col])
            }
          }
        }
        return selectedList
      },
      ...mapState({
        passwordWordList: state => state.user.passwordWordList
      })
    },

    mounted () {
      this.refreshWordlist()
    },

    methods: {
      refreshWordlist () {
        this.$store.dispatch('updatePasswordWordList').then((result) => {
          this.resetWordsSelected()
        })
      },
      resetWordsSelected () {
        this.wordsSelected = []
        for (var row = 0; row < 5; row++) {
          let rowArray = []
          for (var col = 0; col < 5; col++) {
            rowArray.push(false)
          }
          this.wordsSelected.push(rowArray)
        }
      },
      selectWord (row, col) {
        // Convert to 0 based index
        row -= 1
        col -= 1
        // Select/deselect the word and allow vue to detect
        // the changes in the array
        let newRowArr = this.wordsSelected[row]
        newRowArr[col] = !this.wordsSelected[row][col]
        this.wordsSelected.splice(row, 1, newRowArr)
      },
      getRandomInt (min, max) {
        min = Math.ceil(min)
        max = Math.floor(max)
        // The maximum is exclusive and the minimum is inclusive
        return Math.floor(Math.random() * (max - min)) + min
      },
      getRandomBoolean () {
        return Math.random() >= 0.5
      },
      getRandomSymbol () {
        return this.symbolList[this.getRandomInt(0, this.symbolList.length)]
      },
      capitalize (word) {
        if (typeof word !== 'string') return ''
        return word.charAt(0).toUpperCase() + word.slice(1)
      },
      // Returns true if the given character is a number
      isNumeric (char) {
        return !isNaN(parseInt(char, 10))
      },
      // Returns true if at least the min amount of numbers is included
      includesMinNumbers (symbolNumList) {
        let numNumbers = 0
        for (var index in symbolNumList) {
          let cur = symbolNumList[index]
          if (this.isNumeric(cur)) {
            numNumbers += 1
          }
        }
        return numNumbers >= this.minNumbers
      },
      // Returns true if the list contains at least one symbol
      includesSymbol (symbolNumList) {
        for (var index in symbolNumList) {
          let cur = symbolNumList[index]
          if (this.symbolList.includes(cur)) {
            return true
          }
        }
        return false
      },
      generatePassword () {
        let newPassword = ''

        // Get a list of the symbols/numbers to be used
        // Keep trying until we get the min amount of numbers
        let symbolNumList = []
        while (!this.includesMinNumbers(symbolNumList) || !this.includesSymbol(symbolNumList)) {
          symbolNumList = []
          for (var count = 0; count < 4; count++) {
            if (this.getRandomBoolean()) {
              symbolNumList.push(this.getRandomSymbol())
            } else {
              symbolNumList.push(this.getRandomInt(0, 10).toString())
            }
          }
        }
        // Add the first symbol/number
        newPassword = newPassword.concat(symbolNumList[0])
        // Get the wordlist
        let wordsList = this.wordsSelectedList.slice()

        // Build the rest of the password
        for (var i = 0; i < 3; i++) {
          // Add a random selected word
          let selectedWordIndex = this.getRandomInt(0, wordsList.length)
          let selectedWord = this.capitalize(wordsList[selectedWordIndex])
          wordsList.splice(selectedWordIndex, 1)
          newPassword = newPassword.concat(selectedWord)
          newPassword = newPassword.concat(symbolNumList[i + 1])
        }

        this.generatedPasswords.push(newPassword)
      },
      passwordSelected(password, index) {
        this.clickedCopyInput = index;
        this.$emit('password-selected', password);
      }
    }
  }
</script>




// WEBPACK FOOTER //
// PasswordGenerator.vue?262b60d0